import { useEffect, useMemo, useState, useContext } from 'react';
import { useIsMounted } from '../utils/ReactUtils';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Grid, Skeleton, IconButton } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { getRequestConfig } from '../auth/auth.js';
import { ApiGateway } from '../config/config.js';
import axios from 'axios';
import ApprovalCategoryDialog from './ApprovalCategoryDialog.js';
import { default as bus } from '../utils/EventBus';
import Event from '../utils/EventNames.js';
import ConfirmDeleteDialog from './ConfirmDeleteDialog.js';
import CacheContext from '../cache/CacheContext.js'

const cacheKey = 'getAuthorizationApprovalCategories'

export default function ListAuthApprovalCategories(props = { minimal: false }) {
  const isMounted = useIsMounted();
  const cacheContext = useContext(CacheContext)

  const [approvalCategories, setApprovalCategories] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState();
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [confirmDeleteDialogProps, setConfirmDeleteDialogProps] = useState({});

  const openAddDialog = () => {
    setCurrentRow();
    setAddDialogOpen(true);
  };

  useEffect(() => {
    const eventName = 'add-approval-type-or-category';
    bus.addListener(eventName, openAddDialog);
    return () => {
      bus.removeListener(eventName, openAddDialog);
    };
  }, []);

  const submitAddRecord = async (values, isEdit) => {
    cacheContext.removeItem(cacheKey)
    props.startLoading();
    try {
      const requestConfig = await getRequestConfig();
      if (isEdit) {
        const basePath = `${ApiGateway.authorization}/approval-categories/${values.id}`;
        await axios.put(basePath, values, requestConfig);
      } else {
        const basePath = `${ApiGateway.authorization}/approval-categories`;
        await axios.put(basePath, values, requestConfig);
      }
    } catch (err) {
      console.error('err', err?.response?.data);
      bus.emit(Event.ALERT, {
        text: err?.response?.data || err.message,
        style: 'error',
        duration: 3000,
      });
    }

    setAddDialogOpen(false);
    props.stopLoading();
    props.refresh();
  };

  const deleteRecord = async (id) => {
    cacheContext.removeItem(cacheKey)
    props.startLoading();
    try {
      const requestConfig = await getRequestConfig();
      const basePath = `${ApiGateway.authorization}/approval-categories/${id}`;
      await axios.delete(basePath, requestConfig);
    } catch (err) {
      bus.emit(Event.ALERT, {
        text: err?.response?.data || err.message,
        style: 'error',
        duration: 3000,
      });
    }
    props.stopLoading();
    props.refresh();
  };

  const flexProps = {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  };
  const colProps = {
    size: 120,
    maxSize: 200,
    minSize: 40,
  };

  const getAuthorizationApprovalCategories = async () => {
    props.startLoading();
    const hit = cacheContext.getItem(cacheKey)
    if(hit) {
      setApprovalCategories(hit); 
    } else {
      const requestConfig = await getRequestConfig();
      const basePath = `${ApiGateway.authorization}/approval-categories`;

      const response = await axios.get(basePath, requestConfig);
      if (response.data?.results) {
        setApprovalCategories(response.data.results || []);
        cacheContext.setItem(cacheKey, response.data.results || [])
      }
    }
    props.stopLoading();
  };

  useEffect(() => {
    if (isMounted()) {
      getAuthorizationApprovalCategories();
    }
  }, []);

  // Columns match category shape now
  const listColumns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'Category ID',
        editable: false,
        ...colProps,
        filterVariant: 'text',
        muiFilterTextFieldProps: { placeholder: 'Filter by ID' },
        muiTableHeadCellProps: { align: 'left', sx: { ...flexProps } },
        muiTableBodyCellProps: { align: 'left', sx: { ...flexProps } },
      },
      {
        accessorKey: 'label',
        header: 'Label',
        editable: false,
        ...colProps,
        size: 250,
        filterVariant: 'text',
        muiFilterTextFieldProps: { placeholder: 'Filter by label' },
        muiTableHeadCellProps: { align: 'left', sx: { ...flexProps } },
        muiTableBodyCellProps: { align: 'left', sx: { ...flexProps } },
      },
      {
        accessorKey: 'displayOrder',
        header: 'Display Order',
        editable: false,
        ...colProps,
        filterVariant: 'range',
        muiFilterTextFieldProps: { placeholder: 'Filter by order' },
        muiTableHeadCellProps: { align: 'center', sx: { ...flexProps } },
        muiTableBodyCellProps: { align: 'center', sx: { ...flexProps } },
      },
      {
        accessorKey: 'active',
        header: 'Active',
        // grow: 1,
        ...colProps,
        editable: false,
        filterVariant: 'checkbox',
        muiTableHeadCellProps: {
          align: 'center',
          sx: { ...flexProps }
        },
        muiTableBodyCellProps: {
          align: 'center',
          sx: { ...flexProps }
        },
        Cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
      },
      {
        id: 'actions',
        header: 'Actions',
        ...colProps,
        enableColumnFilter: false,
        enableSorting: false,
        muiTableHeadCellProps: { align: 'center', sx: { ...flexProps } },
        muiTableBodyCellProps: { align: 'center', sx: { ...flexProps } },
        Cell: ({ row }) => {
          const handleEdit = () => {
            setCurrentRow(row.original);
            setAddDialogOpen(true);
          };
          const handleDelete = () => {
            setConfirmDeleteDialogProps({
              onConfirm: () => {
                deleteRecord(row.original.id);
                setCurrentRow();
              },
              itemName: row.original.label,
              onClose: () => setConfirmDeleteDialogOpen(false),
            });
            setConfirmDeleteDialogOpen(true);
          };
          return (
            <>
              <IconButton size="small" color="primary" onClick={handleEdit}>
                <Edit fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={handleDelete}>
                <Delete fontSize="small" />
              </IconButton>
            </>
          );
        },
      },
    ],
    [colProps, flexProps]
  );

  const table = useMaterialReactTable({
    layoutMode: 'semantic',
    columns: listColumns,
    data: approvalCategories,
    enableColumnActions: false,
    enableColumnFilters: props.showFilters && !props.minimal,
    initialState: {
      showColumnFilters: !props.minimal,
      pagination: props.minimal
        ? undefined
        : { pageIndex: props.pageIndex ?? 0, pageSize: props.rowsPerPage ?? 15 },
    },
    enablePagination: props.minimal !== true,
    enableSorting: true,
    enableBottomToolbar: true,
    enableEditing: false,
    muiTableContainerProps: props.minimal
      ? { width: '100%' }
      : { sx: { minHeight: 300, width: '100%' } },
    muiTableBodyCellProps: {
      sx: { fontSize: '.82rem', padding: '4px !important', flex: 1, minWidth: 0 },
    },
    renderTopToolbar: false,
  });

  return (
    <>
      <ApprovalCategoryDialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setCurrentRow();
        }}
        onSubmit={submitAddRecord}
        editData={currentRow}
      />
      <ConfirmDeleteDialog
        open={confirmDeleteDialogOpen}
        {...confirmDeleteDialogProps}
      />
      <Grid container rowSpacing={2}>
        <Grid item xs={12}>
          {props.loading && (
            <Skeleton
              variant="rounded"
              animation="wave"
              height={100}
              sx={{ margin: '5px' }}
            />
          )}
          {!props.loading && <MaterialReactTable table={table} />}
        </Grid>
      </Grid>
    </>
  );
}